import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth, API } from '@/App';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { 
  Wallet, Send, QrCode, History, ArrowUpRight, ArrowDownLeft,
  Crown, LogOut, User, Bell, TrendingUp, CreditCard, Plus, Coins
} from 'lucide-react';
import { toast } from 'sonner';

const CitizenDashboard = () => {
  const navigate = useNavigate();
  const { user, logout, token } = useAuth();
  const [wallet, setWallet] = useState(null);
  const [kalWallet, setKalWallet] = useState(null);
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [transferOpen, setTransferOpen] = useState(false);
  const [transferData, setTransferData] = useState({ to_wallet_id: '', amount: '', description: '' });
  const [qrCode, setQrCode] = useState(null);

  const headers = token ? { Authorization: `Bearer ${token}` } : {};

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [walletsRes, transactionsRes] = await Promise.all([
        axios.get(`${API}/wallets`, { headers, withCredentials: true }),
        axios.get(`${API}/wallets/transactions`, { headers, withCredentials: true })
      ]);
      
      const cbdcWallet = walletsRes.data.find(w => w.currency === 'CBDC');
      const kal = walletsRes.data.find(w => w.currency === 'TNB');
      if (cbdcWallet) setWallet(cbdcWallet);
      if (kal) setKalWallet(kal);
      setTransactions(transactionsRes.data);
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleTransfer = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/wallets/transfer`, {
        to_wallet_id: transferData.to_wallet_id,
        amount: parseFloat(transferData.amount),
        description: transferData.description
      }, { headers, withCredentials: true });
      
      toast.success('Transfer completed successfully!');
      setTransferOpen(false);
      setTransferData({ to_wallet_id: '', amount: '', description: '' });
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Transfer failed');
    }
  };

  const generateQR = async () => {
    if (!wallet) return;
    try {
      const response = await axios.get(`${API}/wallets/qr/${wallet.wallet_id}`, { headers, withCredentials: true });
      setQrCode(response.data.qr_code);
    } catch (error) {
      toast.error('Failed to generate QR code');
    }
  };

  const handleLogout = async () => {
    await logout();
    navigate('/');
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="animate-spin h-12 w-12 border-4 border-primary border-t-transparent rounded-full"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="sticky top-0 z-50 glass border-b border-border/50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <Link to="/" className="flex items-center gap-3">
              <div className="w-9 h-9 bg-primary rounded-sm flex items-center justify-center">
                <Crown className="h-5 w-5 text-primary-foreground" />
              </div>
              <span className="font-serif text-lg font-semibold text-primary hidden sm:block">Royal CBDC</span>
            </Link>

            <div className="flex items-center gap-4">
              <Button variant="ghost" size="icon">
                <Bell className="h-5 w-5" />
              </Button>
              <div className="flex items-center gap-3">
                <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center">
                  <User className="h-4 w-4 text-primary" />
                </div>
                <span className="text-sm font-medium hidden sm:block">{user?.name}</span>
              </div>
              <Button variant="ghost" size="icon" onClick={handleLogout} data-testid="logout-btn">
                <LogOut className="h-5 w-5" />
              </Button>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="mb-8">
          <h1 className="font-serif text-2xl md:text-3xl font-normal text-primary">
            Welcome, {user?.name?.split(' ')[0]}
          </h1>
          <p className="text-muted-foreground mt-1">Manage your digital wallet and transactions</p>
        </div>

        {/* Wallet Overview */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-8">
          <Card className="lg:col-span-2 sovereign-card bg-primary text-primary-foreground">
            <CardContent className="p-6">
              <div className="flex justify-between items-start mb-6">
                <div>
                  <p className="text-primary-foreground/70 text-sm uppercase tracking-wider mb-1">Total Balance</p>
                  <p className="font-mono text-4xl md:text-5xl font-semibold" data-testid="wallet-balance">
                    {wallet?.balance?.toLocaleString('en-US', { minimumFractionDigits: 2 })} <span className="text-xl">CBDC</span>
                  </p>
                </div>
                <div className="w-12 h-12 rounded-lg bg-white/10 flex items-center justify-center">
                  <Wallet className="h-6 w-6" />
                </div>
              </div>
              <div className="flex flex-wrap gap-3">
                <Dialog open={transferOpen} onOpenChange={setTransferOpen}>
                  <DialogTrigger asChild>
                    <Button variant="secondary" className="gap-2" data-testid="send-btn">
                      <Send className="h-4 w-4" /> Send
                    </Button>
                  </DialogTrigger>
                  <DialogContent>
                    <DialogHeader>
                      <DialogTitle>Send CBDC</DialogTitle>
                    </DialogHeader>
                    <form onSubmit={handleTransfer} className="space-y-4 mt-4">
                      <div className="space-y-2">
                        <Label>Recipient Wallet ID</Label>
                        <Input
                          placeholder="Enter wallet ID"
                          value={transferData.to_wallet_id}
                          onChange={(e) => setTransferData({ ...transferData, to_wallet_id: e.target.value })}
                          required
                          data-testid="transfer-wallet-input"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label>Amount (CBDC)</Label>
                        <Input
                          type="number"
                          step="0.01"
                          placeholder="0.00"
                          value={transferData.amount}
                          onChange={(e) => setTransferData({ ...transferData, amount: e.target.value })}
                          required
                          data-testid="transfer-amount-input"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label>Description (Optional)</Label>
                        <Input
                          placeholder="Payment for..."
                          value={transferData.description}
                          onChange={(e) => setTransferData({ ...transferData, description: e.target.value })}
                          data-testid="transfer-description-input"
                        />
                      </div>
                      <Button type="submit" className="w-full" data-testid="transfer-submit-btn">
                        Send Payment
                      </Button>
                    </form>
                  </DialogContent>
                </Dialog>

                <Dialog>
                  <DialogTrigger asChild>
                    <Button variant="secondary" className="gap-2" onClick={generateQR} data-testid="receive-btn">
                      <QrCode className="h-4 w-4" /> Receive
                    </Button>
                  </DialogTrigger>
                  <DialogContent>
                    <DialogHeader>
                      <DialogTitle>Receive Payment</DialogTitle>
                    </DialogHeader>
                    <div className="text-center py-4">
                      {qrCode ? (
                        <img src={qrCode} alt="Payment QR Code" className="mx-auto w-48 h-48" />
                      ) : (
                        <div className="w-48 h-48 bg-muted animate-pulse mx-auto rounded-lg"></div>
                      )}
                      <p className="mt-4 text-sm text-muted-foreground">Scan to send payment</p>
                      <p className="font-mono text-xs mt-2 bg-muted p-2 rounded break-all">
                        {wallet?.wallet_id}
                      </p>
                    </div>
                  </DialogContent>
                </Dialog>

                <Link to="/wallet">
                  <Button variant="secondary" className="gap-2" data-testid="history-btn">
                    <History className="h-4 w-4" /> History
                  </Button>
                </Link>
                <Link to="/payments">
                  <Button variant="secondary" className="gap-2" data-testid="payments-btn">
                    <CreditCard className="h-4 w-4" /> Fund / Withdraw
                  </Button>
                </Link>
              </div>
            </CardContent>
          </Card>

          <Card className="sovereign-card">
            <CardContent className="p-6">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 rounded-lg bg-amber-500/10 flex items-center justify-center">
                  <CreditCard className="h-5 w-5 text-amber-600" />
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Wallet ID</p>
                  <p className="font-mono text-xs">{wallet?.wallet_id?.slice(0, 16)}...</p>
                </div>
              </div>
              <div className="space-y-3">
                <div className="flex justify-between text-sm">
                  <span className="text-muted-foreground">Type</span>
                  <span className="font-medium capitalize">{wallet?.wallet_type}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-muted-foreground">Currency</span>
                  <span className="font-medium">{wallet?.currency}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-muted-foreground">Status</span>
                  <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-green-500/10 text-green-600">
                    Active
                  </span>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Quick Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
          {[
            { label: 'TNB Balance', value: kalWallet ? `${kalWallet.balance?.toLocaleString()} KAL` : 'N/A', icon: <Coins className="h-4 w-4 text-gold" />, link: '/tnb/market' },
            { label: 'Transactions', value: transactions.length, icon: <History className="h-4 w-4" /> },
            { label: 'Sent', value: transactions.filter(t => t.from_wallet_id === wallet?.wallet_id).length, icon: <ArrowUpRight className="h-4 w-4 text-red-500" /> },
            { label: 'Received', value: transactions.filter(t => t.to_wallet_id === wallet?.wallet_id).length, icon: <ArrowDownLeft className="h-4 w-4 text-green-500" /> },
          ].map((stat, idx) => (
            <Link to={stat.link || '#'} key={idx}>
              <Card className="dashboard-card hover:border-primary/30">
                <CardContent className="p-4">
                  <div className="flex items-center justify-between">
                    <span className="text-muted-foreground text-sm">{stat.label}</span>
                    {stat.icon}
                  </div>
                  <p className="font-mono text-2xl font-semibold mt-2" data-testid={`stat-${stat.label.toLowerCase().replace(/ /g, '-')}`}>
                    {stat.value}
                  </p>
                </CardContent>
              </Card>
            </Link>
          ))}
        </div>

        {/* Recent Transactions */}
        <Card className="sovereign-card">
          <CardHeader className="flex flex-row items-center justify-between">
            <CardTitle className="font-sans text-lg font-semibold">Recent Transactions</CardTitle>
            <Link to="/wallet">
              <Button variant="ghost" size="sm" className="text-muted-foreground">
                View All
              </Button>
            </Link>
          </CardHeader>
          <CardContent>
            {transactions.length === 0 ? (
              <div className="text-center py-12 text-muted-foreground">
                <History className="h-12 w-12 mx-auto mb-4 opacity-50" />
                <p>No transactions yet</p>
                <p className="text-sm mt-1">Make your first payment to get started</p>
              </div>
            ) : (
              <div className="space-y-3">
                {transactions.slice(0, 5).map((txn, idx) => (
                  <div key={idx} className="flex items-center justify-between p-3 rounded-lg bg-muted/30 hover:bg-muted/50 transition-colors" data-testid={`transaction-${idx}`}>
                    <div className="flex items-center gap-3">
                      <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                        txn.from_wallet_id === wallet?.wallet_id 
                          ? 'bg-red-500/10' 
                          : 'bg-green-500/10'
                      }`}>
                        {txn.from_wallet_id === wallet?.wallet_id 
                          ? <ArrowUpRight className="h-5 w-5 text-red-500" />
                          : <ArrowDownLeft className="h-5 w-5 text-green-500" />
                        }
                      </div>
                      <div>
                        <p className="font-medium text-sm">
                          {txn.from_wallet_id === wallet?.wallet_id ? 'Sent' : 'Received'}
                        </p>
                        <p className="text-xs text-muted-foreground">
                          {txn.description || 'CBDC Transfer'}
                        </p>
                      </div>
                    </div>
                    <div className="text-right">
                      <p className={`font-mono font-medium ${
                        txn.from_wallet_id === wallet?.wallet_id ? 'text-red-500' : 'text-green-500'
                      }`}>
                        {txn.from_wallet_id === wallet?.wallet_id ? '-' : '+'}{txn.amount.toFixed(2)} CBDC
                      </p>
                      <p className="text-xs text-muted-foreground">
                        {new Date(txn.created_at).toLocaleDateString()}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Quick Links */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-8">
          {[
            { label: 'Payments', icon: <CreditCard />, link: '/payments' },
            { label: 'TNB Market', icon: <Coins />, link: '/tnb/market' },
            { label: 'Bond Market', icon: <CreditCard />, link: '/bonds' },
            { label: 'Full Wallet', icon: <Wallet />, link: '/wallet' },
          ].map((item, idx) => (
            <Link to={item.link} key={idx}>
              <Card className="dashboard-card hover:border-primary/30 cursor-pointer">
                <CardContent className="p-4 flex items-center gap-3">
                  <div className="w-10 h-10 rounded-lg bg-primary/5 flex items-center justify-center">
                    {item.icon}
                  </div>
                  <span className="font-medium text-sm">{item.label}</span>
                </CardContent>
              </Card>
            </Link>
          ))}
        </div>
      </main>
    </div>
  );
};

export default CitizenDashboard;
